import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Save, Send, ArrowLeft, Eye, Edit2 } from 'lucide-react';
import { independentQuotesService, independentClientsService } from '../../services/independent';
import IndependentComponentModal from './components/IndependentComponentModal';

const IndependentQuoteBuilderEnhanced = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [activeStep, setActiveStep] = useState('details'); // details, components, preview
  const [formData, setFormData] = useState({
    client_id: '',
    trip_name: '',
    destination: '',
    start_date: '',
    end_date: '',
    adults: 1,
    children: 0,
    total_price: 0,
    deposit_amount: 0,
    deposit_due_date: '',
    notes: '',
    status: 'draft'
  });

  const [components, setComponents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  // Component types organized by category
  const componentCategories = [
    {
      id: 'general',
      name: 'General Travel',
      components: [
        { id: 'hotel', name: 'Hotel', icon: '🏨' },
        { id: 'flight', name: 'Flight', icon: '✈️' },
        { id: 'car', name: 'Car Rental', icon: '🚗' },
        { id: 'restaurant', name: 'Restaurant', icon: '🍽️' },
        { id: 'package', name: 'Package Deal', icon: '📦' },
        { id: 'insurance', name: 'Travel Insurance', icon: '🛡️' },
        { id: 'activity', name: 'Activity/Tour', icon: '🎯' },
        { id: 'general', name: 'General Item', icon: '📋' }
      ]
    },
    {
      id: 'cruises',
      name: 'Cruise Lines',
      components: [
        { id: 'cruise', name: 'Cruise', icon: '🚢' },
        { id: 'disney-cruise-line', name: 'Disney Cruise Line', icon: '🚢' },
        { id: 'royal-caribbean-cruise', name: 'Royal Caribbean', icon: '⚓' },
        { id: 'carnival-cruise', name: 'Carnival Cruise', icon: '🎪' }
      ]
    },
    {
      id: 'packages',
      name: 'Theme Parks & Packages',
      components: [
        { id: 'disney-wdw', name: 'Disney World Package', icon: '🏰' },
        { id: 'disneyland', name: 'Disneyland Package', icon: '🎢' },
        { id: 'universal-orlando', name: 'Universal Orlando', icon: '🎬' },
        { id: 'universal-hollywood', name: 'Universal Hollywood', icon: '🎭' }
      ]
    }
  ];

  useEffect(() => {
    loadClients();
    if (isEditMode) {
      loadQuote();
    }
  }, [id]);

  useEffect(() => {
    calculateTotal();
  }, [components]);

  const loadClients = async () => {
    try {
      const data = await independentClientsService.getAll();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadQuote = async () => {
    try {
      setLoading(true);
      const quote = await independentQuotesService.getById(id);
      setFormData({
        client_id: quote.client_id || '',
        trip_name: quote.trip_name || '',
        destination: quote.destination || '',
        start_date: quote.start_date ? quote.start_date.split('T')[0] : '',
        end_date: quote.end_date ? quote.end_date.split('T')[0] : '',
        adults: quote.adults || 1,
        children: quote.children || 0,
        total_price: quote.total_price || 0,
        deposit_amount: quote.deposit_amount || 0,
        deposit_due_date: quote.deposit_due_date ? quote.deposit_due_date.split('T')[0] : '',
        notes: quote.notes || '',
        status: quote.status || 'draft'
      });
      
      // Parse components if they're stored as JSON string
      const parsedComponents = typeof quote.components === 'string' 
        ? JSON.parse(quote.components) 
        : (quote.components || []);
      setComponents(parsedComponents);
      
      setActiveStep('preview');
    } catch (error) {
      console.error('Error loading quote:', error);
      alert('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openComponentModal = (type) => {
    setEditingComponent({
      type: type,
      title: '',
      description: '',
      price: 0,
      isOptional: false,
      data: {}
    });
    setEditingIndex(null);
    setShowComponentModal(true);
  };

  const editComponent = (index) => {
    setEditingComponent(components[index]);
    setEditingIndex(index);
    setShowComponentModal(true);
  };

  const saveComponent = (componentData) => {
    if (editingIndex !== null) {
      // Update existing component
      const updated = [...components];
      updated[editingIndex] = componentData;
      setComponents(updated);
    } else {
      // Add new component
      setComponents([...components, { ...componentData, id: Date.now() }]);
    }
    setShowComponentModal(false);
    setEditingComponent(null);
    setEditingIndex(null);
  };

  const removeComponent = (index) => {
    if (window.confirm('Remove this component?')) {
      setComponents(components.filter((_, i) => i !== index));
    }
  };

  const calculateTotal = () => {
    const total = components.reduce((sum, comp) => {
      // Only include non-optional components in base price
      if (!comp.isOptional) {
        return sum + (parseFloat(comp.price) || 0);
      }
      return sum;
    }, 0);
    setFormData(prev => ({ ...prev, total_price: total }));
  };

  const handleSave = async (sendToClient = false) => {
    if (!formData.client_id || !formData.trip_name || !formData.destination) {
      alert('Please fill in client, trip name, and destination');
      return;
    }

    setSaving(true);
    try {
      const quoteData = {
        ...formData,
        components: JSON.stringify(components),
        status: sendToClient ? 'sent' : formData.status
      };

      if (isEditMode) {
        await independentQuotesService.update(id, quoteData);
      } else {
        const response = await independentQuotesService.create(quoteData);
        // Update URL for new quotes
        if (response.id) {
          window.history.replaceState(null, '', `/independent/quotes/${response.id}/edit`);
        }
      }

      if (sendToClient) {
        alert('Quote saved and marked as sent!');
      } else {
        alert('Quote saved successfully!');
      }
      
      setActiveStep('preview');
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (!formData.client_id || !formData.trip_name) {
      alert('Please fill in at least client and trip name to preview');
      return;
    }
    setActiveStep('preview');
  };

  const selectedClient = clients.find(c => c.id === parseInt(formData.client_id));
  const optionalComponents = components.filter(c => c.isOptional);
  const requiredComponents = components.filter(c => !c.isOptional);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/independent/quotes')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Edit Quote' : 'Create Quote'}
            </h1>
            <p className="text-gray-600 mt-1">Build a custom travel quote</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {activeStep === 'preview' && (
            <button
              onClick={() => setActiveStep('details')}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Edit2 size={18} className="mr-2" />
              Edit
            </button>
          )}
          {activeStep !== 'preview' && (
            <button
              onClick={handlePreview}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Eye size={18} className="mr-2" />
              Preview
            </button>
          )}
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <Save size={18} className="mr-2" />
            Save Draft
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Send size={18} className="mr-2" />
            Save & Send
          </button>
        </div>
      </div>

      {/* Step Indicator */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={() => setActiveStep('details')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeStep === 'details' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            1. Details
          </button>
          <button
            onClick={() => setActiveStep('components')}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeStep === 'components' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            2. Components ({components.length})
          </button>
          <button
            onClick={handlePreview}
            className={`px-4 py-2 rounded-lg font-medium ${
              activeStep === 'preview' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            3. Preview
          </button>
        </div>
      </div>

      {/* Step Content */}
      {activeStep === 'details' && (
        <>
          {/* Client & Trip Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Client & Trip Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} - {client.email}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trip Name *</label>
                <input
                  type="text"
                  name="trip_name"
                  value={formData.trip_name}
                  onChange={handleChange}
                  placeholder="e.g., Hawaiian Adventure 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination *</label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  placeholder="e.g., Maui, Hawaii"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adults</label>
                <input
                  type="number"
                  name="adults"
                  value={formData.adults}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Children</label>
                <input
                  type="number"
                  name="children"
                  value={formData.children}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Price</label>
                <input
                  type="number"
                  value={formData.total_price}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 font-semibold"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from components</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Amount</label>
                <input
                  type="number"
                  name="deposit_amount"
                  value={formData.deposit_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Due Date</label>
                <input
                  type="date"
                  name="deposit_due_date"
                  value={formData.deposit_due_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Additional Notes</h2>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder="Any additional information..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setActiveStep('components')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Next: Add Components →
            </button>
          </div>
        </>
      )}

      {activeStep === 'components' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Travel Components</h2>
          
          {/* Component Categories */}
          {componentCategories.map(category => (
            <div key={category.id} className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">{category.name}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {category.components.map(comp => (
                  <button
                    key={comp.id}
                    onClick={() => openComponentModal(comp.id)}
                    className="flex items-center justify-center px-4 py-3 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <span className="text-2xl mr-2">{comp.icon}</span>
                    <span className="text-sm font-medium">{comp.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Added Components List */}
          {components.length > 0 && (
            <div className="mt-8">
              <h3 className="font-semibold text-gray-900 mb-4">Added Components ({components.length})</h3>
              <div className="space-y-3">
                {components.map((comp, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-semibold">{comp.title || 'Untitled Component'}</h4>
                        {comp.isOptional && (
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                            Optional
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{comp.description}</p>
                      <p className="text-lg font-semibold text-blue-600 mt-2">
                        ${parseFloat(comp.price || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => editComponent(index)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => removeComponent(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-between mt-6">
            <button
              onClick={() => setActiveStep('details')}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              ← Back to Details
            </button>
            <button
              onClick={handlePreview}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Preview Quote →
            </button>
          </div>
        </div>
      )}

      {activeStep === 'preview' && (
        <div className="bg-white rounded-lg shadow p-8">
          <div className="max-w-4xl mx-auto">
            {/* Quote Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{formData.trip_name}</h1>
              <p className="text-xl text-gray-600">{formData.destination}</p>
            </div>

            {/* Client Info */}
            {selectedClient && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Prepared for:</h3>
                <p className="text-gray-700">
                  {selectedClient.first_name} {selectedClient.last_name}
                </p>
                <p className="text-gray-600">{selectedClient.email}</p>
                {selectedClient.phone && <p className="text-gray-600">{selectedClient.phone}</p>}
              </div>
            )}

            {/* Trip Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-600">Travel Dates</p>
                <p className="font-semibold">
                  {formData.start_date && new Date(formData.start_date).toLocaleDateString()} - {formData.end_date && new Date(formData.end_date).toLocaleDateString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Travelers</p>
                <p className="font-semibold">
                  {formData.adults} Adult{formData.adults !== 1 ? 's' : ''}
                  {formData.children > 0 && `, ${formData.children} Child${formData.children !== 1 ? 'ren' : ''}`}
                </p>
              </div>
            </div>

            {/* Required Components */}
            {requiredComponents.length > 0 && (
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-4">Included in Your Package</h3>
                <div className="space-y-4">
                  {requiredComponents.map((comp, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                      <h4 className="font-semibold text-gray-900">{comp.title}</h4>
                      <p className="text-gray-600 text-sm">{comp.description}</p>
                      <p className="text-blue-600 font-semibold mt-1">
                        ${parseFloat(comp.price || 0).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Optional Components */}
            {optionalComponents.length > 0 && (
              <div className="mb-6 bg-yellow-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">Optional Add-Ons</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enhance your experience with these optional components
                </p>
                <div className="space-y-4">
                  {optionalComponents.map((comp, index) => (
                    <div key={index} className="bg-white border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{comp.title}</h4>
                          <p className="text-gray-600 text-sm">{comp.description}</p>
                        </div>
                        <p className="text-blue-600 font-semibold ml-4">
                          +${parseFloat(comp.price || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pricing Summary */}
            <div className="border-t-2 border-gray-200 pt-6">
              <div className="space-y-2">
                <div className="flex justify-between text-lg">
                  <span className="font-medium">Base Package Price:</span>
                  <span className="font-semibold">${formData.total_price.toLocaleString()}</span>
                </div>
                {formData.deposit_amount > 0 && (
                  <>
                    <div className="flex justify-between text-gray-600">
                      <span>Deposit Required:</span>
                      <span>${formData.deposit_amount.toLocaleString()}</span>
                    </div>
                    {formData.deposit_due_date && (
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Deposit Due:</span>
                        <span>{new Date(formData.deposit_due_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Notes */}
            {formData.notes && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Additional Information</h4>
                <p className="text-gray-700 whitespace-pre-wrap">{formData.notes}</p>
              </div>
            )}

            <div className="flex justify-center mt-8">
              <button
                onClick={() => setActiveStep('components')}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                ← Edit Components
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Component Modal */}
      {showComponentModal && (
        <IndependentComponentModal
          component={editingComponent}
          onSave={saveComponent}
          onCancel={() => {
            setShowComponentModal(false);
            setEditingComponent(null);
            setEditingIndex(null);
          }}
        />
      )}
    </div>
  );
};

export default IndependentQuoteBuilderEnhanced;
